import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Play, Pause, MessageSquare, Clock, Sparkles, Loader2, ArrowLeft, User, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getTierColor = (tier) => {
    const colors = {
        rookie: 'bg-zinc-600',
        contender: 'bg-blue-500',
        prospect: 'bg-violet-500',
        pro: 'bg-fuchsia-500',
        elite: 'bg-amber-500',
        legend: 'bg-[#7FFF00] text-black'
    };
    return colors[tier] || colors.rookie;
};

const getScoreColor = (score) => {
    if (score >= 9) return 'text-truth';
    if (score >= 7) return 'text-amber-400';
    if (score >= 5) return 'text-blue-400';
    return 'text-zinc-400';
};

const PerformanceDetailPage = () => {
    const { performanceId } = useParams();
    const { user, token, isAuthenticated } = useAuth();
    
    const [performance, setPerformance] = useState(null);
    const [judgments, setJudgments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = useRef(null);
    
    // Judgment form
    const [showJudgmentForm, setShowJudgmentForm] = useState(false);
    const [judgmentData, setJudgmentData] = useState({
        flow: 5,
        cadence: 5,
        delivery: 5,
        clarity: 5,
        comment: ''
    });
    const [submitting, setSubmitting] = useState(false);
    
    // AI Judgment
    const [aiLoading, setAiLoading] = useState(false);
    const [aiJudgment, setAiJudgment] = useState(null);
    const [aiUnavailable, setAiUnavailable] = useState(false);

    useEffect(() => {
        fetchPerformance();
        fetchJudgments();
    }, [performanceId]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        const onTime = () => {
            setCurrentTime(audio.currentTime);
            setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
        };
        const onLoad = () => setDuration(audio.duration || 0);
        const onEnd = () => { setIsPlaying(false); setProgress(0); setCurrentTime(0); };
        audio.addEventListener('timeupdate', onTime);
        audio.addEventListener('loadedmetadata', onLoad);
        audio.addEventListener('ended', onEnd);
        return () => {
            audio.removeEventListener('timeupdate', onTime);
            audio.removeEventListener('loadedmetadata', onLoad);
            audio.removeEventListener('ended', onEnd);
        };
    }, []);

    const fetchPerformance = async () => {
        try {
            const response = await axios.get(`${API}/performances/${performanceId}`);
            setPerformance(response.data);
        } catch (error) {
            console.error('Failed to fetch performance:', error);
            toast.error('Performance not found');
        } finally {
            setLoading(false);
        }
    };

    const fetchJudgments = async () => {
        try {
            const response = await axios.get(`${API}/performances/${performanceId}/judgments`);
            setJudgments(response.data);
        } catch (error) {
            console.error('Failed to fetch judgments:', error);
        }
    };

    const handleSubmitJudgment = async (e) => {
        e.preventDefault();
        if (!isAuthenticated) {
            toast.error('Please login to judge');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(
                `${API}/judgments`,
                {
                    performance_id: performanceId,
                    ...judgmentData
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Judgment submitted!');
            setShowJudgmentForm(false);
            fetchPerformance();
            fetchJudgments();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit judgment');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAIJudgment = async () => {
        if (!isAuthenticated) {
            toast.error('Please login to use AI judgment');
            return;
        }

        setAiLoading(true);
        try {
            const response = await axios.post(
                `${API}/ai-judgment`,
                { performance_id: performanceId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setAiJudgment(response.data.response);
            toast.success('AI analysis complete');
        } catch (error) {
            const status = error.response?.status;
            if (status === 503) {
                setAiUnavailable(true);
                toast.info('AI analysis coming soon');
                return;
            }
            toast.error(error.response?.data?.detail || 'AI analysis failed');
        } finally {
            setAiLoading(false);
        }
    };

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (isPlaying) { audio.pause(); setIsPlaying(false); }
        else { audio.play().catch(() => {}); setIsPlaying(true); }
    };

    const seekTo = (e) => {
        const audio = audioRef.current;
        if (!audio || !audio.duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = (e.clientX - rect.left) / rect.width;
        audio.currentTime = ratio * audio.duration;
    };

    const fmtTime = (s) => {
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec.toString().padStart(2, '0')}`;
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const canJudge = isAuthenticated && user?.user_id !== performance?.user_id;
    const hasJudged = judgments.some(j => j.judge_user_id === user?.user_id);
    const isOwner = user?.user_id === performance?.user_id;

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-8">
                <Skeleton className="h-8 w-48 mb-4 bg-white/5" />
                <Skeleton className="h-4 w-32 mb-8 bg-white/5" />
                <Skeleton className="h-48 w-full mb-8 bg-white/5" />
                <div className="grid grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-24 bg-white/5" />
                    ))}
                </div>
            </div>
        );
    }

    if (!performance) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-8 text-center">
                <h1 className="font-display font-bold text-2xl mb-4">Performance Not Found</h1>
                <Link to="/feed">
                    <Button variant="outline" className="rounded-sm">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Feed
                    </Button>
                </Link>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Back Link */}
            <Link to="/feed" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Back to Feed
            </Link>

            {/* Header */}
            <div className="mb-8">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h1 className="font-display font-bold text-2xl sm:text-3xl mb-2" data-testid="performance-title">
                            {performance.title}
                        </h1>
                        <Link 
                            to={`/profile/${performance.user_id}`}
                            className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2"
                        >
                            <User className="w-4 h-4" />
                            {performance.artist_name}
                        </Link>
                        {performance.location && (
                            <p className="text-sm text-zinc-500 flex items-center gap-1 mt-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {performance.location}
                            </p>
                        )}
                    </div>
                    {performance.performance_type === 'freestyle' && (
                        <Badge variant="outline" className="text-xs font-mono uppercase">
                            Freestyle
                        </Badge>
                    )}
                </div>

                {performance.description && (
                    <p className="text-zinc-400 mb-4">{performance.description}</p>
                )}

                <div className="flex items-center gap-4 text-sm text-zinc-500">
                    <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(performance.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                        <MessageSquare className="w-4 h-4" />
                        {performance.judgment_count} judgments
                    </span>
                </div>
            </div>

            {/* Audio Player */}
            {(() => {
                const audioSrc = performance.audio_url
                    ? performance.audio_url.startsWith('http')
                        ? performance.audio_url
                        : `${BACKEND_URL}${performance.audio_url}`
                    : null;
                const hasAudio = !!audioSrc;
                const CANDY = ['#00E5FF', '#FF3DCB', '#8B5CFF'];
                const waveHeights = Array.from({ length: 80 }, (_, i) => {
                    const seed = performanceId || 'x';
                    const c = seed.charCodeAt(i % seed.length) || 65;
                    return 14 + ((c * 37 + i * 73) % 66);
                });
                return (
                    <div className="candy-card rounded-2xl p-5 mb-8" data-testid="audio-player">
                        {hasAudio && <audio ref={audioRef} src={audioSrc} preload="metadata" />}

                        {/* Waveform */}
                        <div
                            className="flex items-center gap-px h-20 mb-3 cursor-pointer"
                            onClick={hasAudio ? seekTo : undefined}
                            title={hasAudio ? 'Click to seek' : undefined}
                        >
                            {waveHeights.map((h, i) => {
                                const barProgress = i / waveHeights.length;
                                const played = barProgress <= progress;
                                return (
                                    <div
                                        key={i}
                                        className="flex-1 rounded-sm"
                                        style={{
                                            height: `${h}%`,
                                            backgroundColor: hasAudio ? CANDY[i % 3] : '#27272a',
                                            opacity: hasAudio ? (played ? 0.9 : 0.25) : 0.15,
                                            transition: 'opacity 80ms',
                                        }}
                                    />
                                );
                            })}
                        </div>

                        {/* Controls */}
                        <div className="flex items-center gap-4">
                            <button
                                onClick={hasAudio ? togglePlay : undefined}
                                className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200"
                                style={{
                                    background: hasAudio ? '#39FF14' : '#27272a',
                                    color: hasAudio ? '#04120a' : '#52525b',
                                    boxShadow: isPlaying ? '0 0 22px rgba(57,255,20,.6)' : hasAudio ? '0 0 10px rgba(57,255,20,.3)' : 'none',
                                    cursor: hasAudio ? 'pointer' : 'default',
                                }}
                                data-testid="play-pause-btn"
                            >
                                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                            </button>

                            {hasAudio ? (
                                <div className="flex-1 flex items-center gap-2 min-w-0">
                                    <span className="text-xs font-mono text-zinc-400 w-10 text-right flex-shrink-0">{fmtTime(currentTime)}</span>
                                    <div
                                        className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden cursor-pointer"
                                        onClick={seekTo}
                                    >
                                        <div
                                            className="h-full rounded-full transition-all duration-75"
                                            style={{ width: `${progress * 100}%`, background: '#39FF14' }}
                                        />
                                    </div>
                                    <span className="text-xs font-mono text-zinc-400 w-10 flex-shrink-0">{fmtTime(duration)}</span>
                                </div>
                            ) : (
                                <p className="text-xs text-zinc-600 italic">No audio uploaded for this performance</p>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* Scores */}
            <div className="glass-panel rounded-sm p-6 mb-8" data-testid="scores-panel">
                <h3 className="font-display font-bold mb-4">Skill Ratings</h3>
                
                {performance.judgment_count > 0 ? (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                            {[
                                { label: 'Flow', value: performance.avg_flow },
                                { label: 'Cadence', value: performance.avg_cadence },
                                { label: 'Delivery', value: performance.avg_delivery },
                                { label: 'Clarity', value: performance.avg_clarity },
                            ].map((score) => (
                                <div key={score.label} className="text-center p-4 bg-black/30 rounded-sm">
                                    <p className={`font-mono text-3xl font-bold ${getScoreColor(score.value)}`}>
                                        {score.value.toFixed(1)}
                                    </p>
                                    <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{score.label}</p>
                                </div>
                            ))}
                        </div>

                        <div className="flex items-center justify-center gap-4 pt-4 border-t border-white/10">
                            <span className="text-zinc-400">Overall Score</span>
                            <span className={`font-mono text-4xl font-bold ${getScoreColor(performance.overall_score)}`}>
                                {performance.overall_score.toFixed(1)}
                            </span>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-zinc-400 mb-2">No ratings yet</p>
                        <p className="text-sm text-zinc-500">Be the first to judge this performance</p>
                    </div>
                )}
            </div>

            {/* AI Judgment (Owner Only) */}
            {isOwner && (
                <div className="glass-panel rounded-sm p-6 mb-8 border-primary/30" data-testid="ai-judgment-section">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display font-bold flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-primary" />
                            AI Feedback
                        </h3>
                        <Badge variant="outline" className="text-xs">Private</Badge>
                    </div>

                    {aiJudgment ? (
                        <div className="bg-black/30 rounded-sm p-4">
                            <pre className="whitespace-pre-wrap text-sm text-zinc-300 font-mono">
                                {aiJudgment}
                            </pre>
                        </div>
                    ) : (
                        <div className="text-center py-6">
                            <p className="text-zinc-400 mb-4 text-sm">
                                Get private AI analysis of your performance. This won't affect your public ratings.
                            </p>
                            {aiUnavailable && (
                                <p className="text-xs text-zinc-500 mb-3">
                                    AI analysis is temporarily unavailable. We’re tuning the system—check back soon.
                                </p>
                            )}
                            <Button
                                onClick={handleAIJudgment}
                                disabled={aiLoading || aiUnavailable}
                                variant="outline"
                                className="border-primary/50 hover:bg-primary/10 rounded-sm"
                                data-testid="ai-judgment-btn"
                            >
                                {aiLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Analyzing...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-4 h-4 mr-2" />
                                        {aiUnavailable ? 'AI Coming Soon' : 'Get AI Analysis'}
                                    </>
                                )}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* Submit Judgment */}
            {canJudge && !hasJudged && (
                <div className="glass-panel rounded-sm p-6 mb-8" data-testid="judgment-form-section">
                    {!showJudgmentForm ? (
                        <div className="text-center py-4">
                            <Button
                                onClick={() => setShowJudgmentForm(true)}
                                className="bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest rounded-sm"
                                data-testid="start-judgment-btn"
                            >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Judge This Performance
                            </Button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmitJudgment}>
                            <h3 className="font-display font-bold mb-6">Rate This Performance</h3>
                            
                            <div className="space-y-6 mb-6">
                                {[
                                    { key: 'flow', label: 'Flow', desc: 'Rhythm, timing, how words ride the beat' },
                                    { key: 'cadence', label: 'Cadence', desc: 'Variation in delivery speed and pattern' },
                                    { key: 'delivery', label: 'Delivery', desc: 'Energy, emotion, conviction' },
                                    { key: 'clarity', label: 'Clarity', desc: 'How clear and understandable the words are' },
                                ].map((category) => (
                                    <div key={category.key}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div>
                                                <span className="font-medium">{category.label}</span>
                                                <p className="text-xs text-zinc-500">{category.desc}</p>
                                            </div>
                                            <span className={`font-mono text-xl font-bold ${getScoreColor(judgmentData[category.key])}`}>
                                                {judgmentData[category.key]}
                                            </span>
                                        </div>
                                        <Slider
                                            value={[judgmentData[category.key]]}
                                            onValueChange={([value]) => setJudgmentData({ ...judgmentData, [category.key]: value })}
                                            min={1}
                                            max={10}
                                            step={1}
                                            className="w-full"
                                            data-testid={`slider-${category.key}`}
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="mb-6">
                                <label className="text-sm text-zinc-400 block mb-2">Comment (Optional)</label>
                                <Textarea
                                    value={judgmentData.comment}
                                    onChange={(e) => setJudgmentData({ ...judgmentData, comment: e.target.value })}
                                    placeholder="Give constructive feedback..."
                                    rows={3}
                                    className="bg-black/50 border-white/10 focus:border-primary/50 rounded-sm resize-none"
                                    data-testid="judgment-comment"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    className="bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest rounded-sm"
                                    data-testid="submit-judgment-btn"
                                >
                                    {submitting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        'Submit Judgment'
                                    )}
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => setShowJudgmentForm(false)}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    )}
                </div>
            )}

            {hasJudged && (
                <div className="glass-panel rounded-sm p-4 mb-8 border-[#7FFF00]/30 bg-[#7FFF00]/5">
                    <p className="text-sm text-center text-truth">You've already judged this performance</p>
                </div>
            )}

            {/* Judgments List */}
            <div className="glass-panel rounded-sm p-6" data-testid="judgments-list">
                <h3 className="font-display font-bold mb-6">
                    Community Judgments ({judgments.length})
                </h3>

                {judgments.length > 0 ? (
                    <div className="space-y-4">
                        {judgments.map((judgment) => (
                            <div 
                                key={judgment.judgment_id}
                                className="p-4 bg-black/30 rounded-sm"
                                data-testid={`judgment-${judgment.judgment_id}`}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <Link 
                                        to={`/profile/${judgment.judge_user_id}`}
                                        className="flex items-center gap-2 hover:text-primary transition-colors"
                                    >
                                        <Avatar className="h-8 w-8">
                                            <AvatarFallback className="bg-paper text-xs font-mono">
                                                {judgment.judge_username.slice(0, 2).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="font-medium">{judgment.judge_username}</span>
                                    </Link>
                                    <span className={`font-mono text-lg font-bold ${getScoreColor(judgment.overall)}`}>
                                        {judgment.overall.toFixed(1)}
                                    </span>
                                </div>

                                <div className="grid grid-cols-4 gap-2 mb-3">
                                    {[
                                        { label: 'Flow', value: judgment.flow },
                                        { label: 'Cadence', value: judgment.cadence },
                                        { label: 'Delivery', value: judgment.delivery },
                                        { label: 'Clarity', value: judgment.clarity },
                                    ].map((score) => (
                                        <div key={score.label} className="text-center">
                                            <p className={`font-mono text-sm font-bold ${getScoreColor(score.value)}`}>
                                                {score.value}
                                            </p>
                                            <p className="text-[10px] text-zinc-500">{score.label}</p>
                                        </div>
                                    ))}
                                </div>

                                {judgment.comment && (
                                    <p className="text-sm text-zinc-400 border-t border-white/5 pt-3 mt-3">
                                        "{judgment.comment}"
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <MessageSquare className="w-12 h-12 mx-auto text-zinc-600 mb-3" />
                        <p className="text-zinc-400">No judgments yet</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PerformanceDetailPage;
